import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Stack,
  TextInput,
  Textarea,
  Title,
  useMatches,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useLocalStorage } from "@mantine/hooks";
import dayjs from "dayjs";
import { isEmpty, uniq } from "lodash";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrencyInput, Dropdown, FormField } from "@/components/elements";
import { ConditionsTooltipContent } from "@/components/elements/TooltipContent";
import { IconMinus, IconPlus } from "@/components/icons";
import { defaultCurrency } from "@/lib/currency";
import { newCarSchema } from "@/services/cars/create-car";
import {
  useCarFamilies,
  useCarGenerations,
  useCarMakes,
  useCarModels,
  useCarVariants,
} from "@/services/cars/get-cars-models";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { useProfile } from "@/services/profile/get-profile";
import { formatDate } from "@/utils/formatDate";

import type { CreateCarInput } from "@/services/cars/create-car";
import type { CarVariant } from "@/services/cars/get-cars-models";
import type { Currency } from "@/types/currency";
import type { FC } from "react";

export type CreateCarFormProps = {
  onSubmit: (values: CreateCarInput) => void;
  onCancel?: () => void;
  initialValues?: Partial<CreateCarInput>;
  loading?: boolean;
  labels?: {
    cancelButton?: string;
    submitButton?: string;
  };
  showLessElements?: boolean;
};

export const CreateCarForm: FC<CreateCarFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  loading = false,
  showLessElements = false,
  labels,
}) => {
  const { t } = useTranslation();
  const [minProductionYear, setMinProductionYear] = useState<number>(1900);
  const [maxProductionYear, setMaxProductionYear] = useState<number>(new Date().getFullYear());
  const [showAdditional, setShowAdditional] = useState<boolean>(false);

  const { data: user } = useProfile();
  const { data: plans } = usePlansUser();

  const firstName = user?.additional_info?.first_name ?? user?.name ?? "";
  const lastName = user?.additional_info?.last_name ?? "";
  const nameParts = [firstName, lastName].filter((p) => p !== undefined && p !== null && p !== "");
  const fullName = nameParts.join(" ");
  const isFreePlan = plans?.name === "starter";

  const NOVARIANT = "no_variant";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const form = useForm<CreateCarInput>({
    initialValues: {
      nickname: "",
      owner: fullName || "",
      purchase_price: 0,
      purchase_date: dayjs(new Date()).format("YYYY-MM-DD"),
      production_year: new Date().getFullYear(),
      make: "",
      family: "",
      generation: "",
      model: "",
      variant: NOVARIANT,
      licence_plate: "",
      country_of_registration: "",
      color: "",
      interior_colors: "",
      condition: "",
      number_of_previous_owner: 0,
      status: "owned",
      special_equipment: "",
      vin: "",
      ...initialValues,
    },
    validate: zod4Resolver(newCarSchema),
    validateInputOnBlur: true,
  });

  const { data: carMakes, isLoading: isLoadingMakes } = useCarMakes();

  const { data: carFamilies, isLoading: isLoadingFamilies } = useCarFamilies({
    make: form.values.make,
  });

  const { data: carGenerations, isLoading: isLoadingGenerations } = useCarGenerations({
    make: form.values.make,
    family: form.values.family,
  });

  const { data: carModels, isLoading: isLoadingModels } = useCarModels({
    make: form.values.make,
    family: form.values.family,
    generation: form.values.generation,
  });

  const { data: carVariants, isLoading: isLoadingVariants } = useCarVariants({
    make: form.values.make,
    family: form.values.family,
    generation: form.values.generation,
    model: form.values.model,
  });

  const makeOptions =
    carMakes?.map((item) => ({
      value: item.make,
      label: item.make,
    })) || [];

  const familyOptions =
    carFamilies?.map((item) => ({
      value: item.family,
      label: item.family,
    })) || [];

  const generationOptions =
    carGenerations?.map((item) => ({
      value: item.generation,
      label: item.generation,
    })) || [];

  const modelOptions =
    uniq(carModels?.map((model) => model.model)).map((item) => ({
      value: item,
      label: item,
    })) || [];

  const variantOptions = carVariants
    ? carVariants.map((item) => ({
        value: item.variant || NOVARIANT,
        label: item.variant || t("car.no_variant"),
      }))
    : [];

  const handleMakeChange = (value?: string | null) => {
    form.setFieldValue("make", value || "");
    form.setFieldValue("family", "");
    form.setFieldValue("generation", "");
    form.setFieldValue("model", "");
    form.setFieldValue("variant", NOVARIANT);
  };

  const handleFamilyChange = (value?: string | null) => {
    form.setFieldValue("family", value || "");
    form.setFieldValue("generation", "");
    form.setFieldValue("model", "");
    form.setFieldValue("variant", NOVARIANT);
  };

  const handleGenerationChange = (value?: string | null) => {
    form.setFieldValue("generation", value || "");
    form.setFieldValue("model", "");
    form.setFieldValue("variant", NOVARIANT);
  };

  const handleModelChange = (value?: string | null) => {
    form.setFieldValue("model", value || "");
    form.setFieldValue("variant", NOVARIANT);
    setMinProductionYear(1900);
    setMaxProductionYear(new Date().getFullYear());
  };

  const setMinMaxProdYearsByVariant = (variant: string) => {
    if (!carVariants) return;
    const currentYear = new Date().getFullYear();

    if (variant === NOVARIANT) {
      const baseVariant = carVariants.find((item) => !item.variant);
      setMinProductionYear(baseVariant?.start_of_production || 1900);

      const endYear =
        baseVariant?.end_of_production === 9999
          ? currentYear
          : baseVariant?.end_of_production || currentYear;

      setMaxProductionYear(endYear);
      form.isDirty() && form.setFieldValue("production_year", endYear);
    } else {
      const selectedVariant = carVariants.find((item) => item.variant === variant);
      setMinProductionYear(selectedVariant?.start_of_production || 1900);

      const endYear =
        selectedVariant?.end_of_production === 9999
          ? currentYear
          : selectedVariant?.end_of_production || currentYear;

      setMaxProductionYear(endYear);
      form.isDirty() && form.setFieldValue("production_year", endYear);
    }
  };

  const selectDefaultVariant = (carVariants: CarVariant[]) => {
    const sortedVariants = carVariants.sort((a, b) => {
      const aSplit = a.taxonomy_id.split(".");
      const bSplit = b.taxonomy_id.split(".");

      if (aSplit.length !== bSplit.length) {
        return aSplit.length - bSplit.length;
      }

      return parseInt(aSplit[aSplit.length - 1], 10) - parseInt(bSplit[bSplit.length - 1], 10);
    });
    const variantToSet = sortedVariants[0].variant ? sortedVariants[0].variant : NOVARIANT;
    form.setFieldValue("variant", variantToSet);
    setMinMaxProdYearsByVariant(variantToSet);
  };

  const handleVariantChange = (value?: string | null) => {
    form.setFieldValue("variant", value || NOVARIANT);
    setMinMaxProdYearsByVariant(value || NOVARIANT);
  };

  useEffect(() => {
    if (carVariants) selectDefaultVariant(carVariants);
  }, [carVariants]);

  const handleSubmit = (values: CreateCarInput) => {
    onSubmit({
      ...values,
      purchase_date: dayjs(values.purchase_date).format("YYYY-MM-DD"),
      variant: values.variant === NOVARIANT ? "" : values.variant,
      condition: values.condition ? values.condition : "",
    });
  };

  const isMobile = useMatches({ base: true, md: false });
  const WrapperForFields = isMobile ? Stack : Group;
  const wrapperProps = isMobile ? { gap: "md" } : { grow: true, align: "flex-start" };

  const isEdit = !isEmpty(initialValues);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Title order={4}>{t("components.CreateCarForm.titles.main")}</Title>

        <WrapperForFields {...wrapperProps}>
          {!isEdit ? (
            <>
              <FormField required>
                <Dropdown
                  label={t("car.status")}
                  showLessElements={showLessElements}
                  placeholder={t("components.CreateCarForm.placeholders.status")}
                  data={[
                    { value: "owned", label: t("car.statuses.owned") },
                    { value: "sold", label: t("car.statuses.sold") },
                    { value: "traded", label: t("car.statuses.traded") },
                    { value: "totaled", label: t("car.statuses.totaled") },
                  ]}
                  {...form.getInputProps("status")}
                />
              </FormField>
            </>
          ) : null}
          <FormField required>
            <TextInput
              label={t("car.owner")}
              placeholder={t("components.CreateCarForm.placeholders.owner")}
              {...form.getInputProps("owner")}
            />
          </FormField>
          {isEdit ? <Box>&nbsp;</Box> : null}
        </WrapperForFields>

        <WrapperForFields {...wrapperProps}>
          <FormField required>
            <CurrencyInput
              label={t("car.purchase_price")}
              value={(Number(form.values.purchase_price) || 0) * (Number(currency.value) || 1)}
              placeholder={t("components.CreateCarForm.placeholders.purchase_price", "Price")}
              currencySymbol={currency.symbol}
              onChange={(value) => {
                const adjustedValue = (Number(value) || 0) / (Number(currency.value) || 1);
                form.setFieldValue("purchase_price", adjustedValue);
              }}
            />
          </FormField>
          <FormField required>
            <DatePickerInput
              label={t("car.purchase_date")}
              placeholder={t("components.CreateCarForm.placeholders.purchase_date", "Select date")}
              valueFormat="YYYY-MM-DD"
              valueFormatter={(value) => formatDate(value.date as string)}
              {...form.getInputProps("purchase_date")}
            />
          </FormField>
        </WrapperForFields>

        <WrapperForFields {...wrapperProps}>
          <FormField required>
            <Dropdown
              label={t("car.make")}
              placeholder={t("components.CreateCarForm.placeholders.make")}
              data={makeOptions}
              searchable
              showLessElements={showLessElements}
              disabled={isLoadingMakes}
              onChange={handleMakeChange}
              value={form.values.make}
              error={form.errors.make}
            />
          </FormField>
          <FormField required>
            <Dropdown
              label={t("car.family")}
              placeholder={t("components.CreateCarForm.placeholders.family")}
              data={familyOptions}
              searchable
              showLessElements={showLessElements}
              disabled={isLoadingFamilies || !form.values.make}
              onChange={handleFamilyChange}
              value={form.values.family}
              error={form.errors.family}
            />
          </FormField>
        </WrapperForFields>

        <WrapperForFields {...wrapperProps}>
          <FormField required>
            <Dropdown
              label={t("car.generation")}
              placeholder={t("components.CreateCarForm.placeholders.generation")}
              data={generationOptions}
              searchable
              showLessElements={showLessElements}
              disabled={isLoadingGenerations || !form.values.make || !form.values.family}
              onChange={handleGenerationChange}
              value={form.values.generation}
              error={form.errors.generation}
            />
          </FormField>
          <FormField required>
            <Dropdown
              label={t("car.model")}
              placeholder={t("components.CreateCarForm.placeholders.model")}
              data={modelOptions}
              searchable
              showLessElements={showLessElements}
              disabled={
                isLoadingModels ||
                !form.values.make ||
                !form.values.family ||
                !form.values.generation
              }
              onChange={handleModelChange}
              value={form.values.model}
              error={form.errors.model}
            />
          </FormField>
        </WrapperForFields>

        <WrapperForFields {...wrapperProps}>
          <FormField required>
            <Dropdown
              label={t("car.variant")}
              placeholder={t("components.CreateCarForm.placeholders.variant")}
              data={variantOptions}
              searchable
              showLessElements={showLessElements}
              disabled={
                isLoadingVariants ||
                !form.values.make ||
                !form.values.family ||
                !form.values.generation ||
                !form.values.model
              }
              onChange={handleVariantChange}
              value={form.values.variant}
              error={form.errors.variant}
            />
          </FormField>
          <FormField required>
            <NumberInput
              label={t("car.production_year")}
              placeholder={t("components.CreateCarForm.placeholders.production_year")}
              min={minProductionYear}
              max={maxProductionYear}
              disabled={
                !form.values.make ||
                !form.values.family ||
                !form.values.generation ||
                !form.values.model ||
                !form.values.variant
              }
              {...form.getInputProps("production_year")}
            />
          </FormField>
        </WrapperForFields>

        <Divider my="lg" />

        <Group>
          <Title order={4}>{t("components.CreateCarForm.titles.details")}</Title>
          <ActionIcon size="md" onClick={() => setShowAdditional(!showAdditional)}>
            {showAdditional ? <IconMinus /> : <IconPlus />}
          </ActionIcon>
        </Group>

        {showAdditional && (
          <>
            <WrapperForFields {...wrapperProps}>
              <TextInput
                label={t("car.licence_plate")}
                placeholder={t("components.CreateCarForm.placeholders.licence_plate")}
                {...form.getInputProps("licence_plate")}
              />
              <TextInput
                label={t("car.country_of_registration")}
                placeholder={t("components.CreateCarForm.placeholders.country_of_registration")}
                {...form.getInputProps("country_of_registration")}
              />
            </WrapperForFields>

            <WrapperForFields {...wrapperProps}>
              <FormField>
                <TextInput
                  label={t("car.vin")}
                  placeholder={t("components.CreateCarForm.placeholders.vin")}
                  {...form.getInputProps("vin")}
                />
              </FormField>

              <FormField>
                <TextInput
                  label={t("car.color")}
                  placeholder={t("components.CreateCarForm.placeholders.color")}
                  {...form.getInputProps("color")}
                />
              </FormField>
            </WrapperForFields>
            <WrapperForFields {...wrapperProps}>
              <FormField>
                <TextInput
                  label={t("car.interior_colors")}
                  placeholder={t("components.CreateCarForm.placeholders.interior_colors")}
                  {...form.getInputProps("interior_colors")}
                />
              </FormField>
              <Dropdown
                label={t("car.condition")}
                tooltip={<ConditionsTooltipContent />}
                disabled={isFreePlan}
                clearable
                showLessElements={showLessElements}
                placeholder={t("components.CreateCarForm.placeholders.condition")}
                data={[
                  { value: "superb", label: t("car.conditions.superb") },
                  { value: "excellent", label: t("car.conditions.excellent") },
                  { value: "good", label: t("car.conditions.good") },
                  { value: "fair", label: t("car.conditions.fair") },
                ]}
                value={form.values.condition}
                onChange={(value) => {
                  // Check that it is one of the allowed options
                  const allowedValues = ["superb", "excellent", "good", "fair"] as const;
                  const safeValue = allowedValues.includes(value as any)
                    ? (value as (typeof allowedValues)[number])
                    : undefined;

                  form.setFieldValue("condition", safeValue);
                }}
                error={form.errors.condition}
              />
            </WrapperForFields>

            <WrapperForFields {...wrapperProps}>
              <NumberInput
                label={t("car.number_of_previous_owner")}
                placeholder={t("components.CreateCarForm.placeholders.number_of_previous_owner")}
                min={0}
                {...form.getInputProps("number_of_previous_owner")}
              />
              <TextInput
                label={t("car.nickname")}
                placeholder={t("components.CreateCarForm.placeholders.nickname")}
                {...form.getInputProps("nickname")}
              />
            </WrapperForFields>

            <Textarea
              label={t("car.special_equipment")}
              placeholder={t("components.CreateCarForm.placeholders.special_equipment")}
              minRows={4}
              {...form.getInputProps("special_equipment")}
            />
          </>
        )}

        <Group justify="center" mt="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("components.Form.cancelButton")}
            >
              {labels?.cancelButton || t("components.Form.cancelButton")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={!form.isValid() || loading || !form.isDirty()}
            loading={loading}
            aria-label={t("components.CreateCarForm.submitButton")}
          >
            {labels?.submitButton || t("components.CreateCarForm.submitButton")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
